import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCurrency, formatDaysUntil, jurisdictionLabel, urgencyLabel } from '../lib/format';
import { Loading, Empty, StatusPill } from '../components/Shared';

export function Dashboard() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState<any[] | null>(null);
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    api.entities.list().then((r) => {
      setEntities(r.entities);
      if (r.entities.length === 0) {
        navigate('/onboarding');
      }
    });
    api.obligations.summary().then(setSummary);
  }, [navigate]);

  if (!entities || !summary) return <Loading label="Cargando tu compliance" />;
  if (entities.length === 0) return null;

  const entity = entities[0];
  const totalPenalty = summary.total_max_penalty ?? 0;

  const criticalItems = [...(summary.overdue ?? []), ...(summary.critical ?? [])];
  const warningItems = summary.warning ?? [];

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">{entity.state} · {entity.structure === 'single_member' ? 'Single-Member' : 'Multi-Member'} · Foreign-Owned</div>
        <h1 className="page-title">
          <em>{entity.legal_name}</em>
        </h1>
        <p className="page-description">
          Tu exposición total a multas por incumplimiento suma{' '}
          <strong className="numeric" style={{ color: 'var(--danger-bright)' }}>
            {formatCurrency(totalPenalty)}
          </strong>
          . El compliance bien hecho cuesta menos de 1% de eso.
        </p>
      </div>

      {/* Stats grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem',
        }}
      >
        <StatCard label="Vencidos" value={summary.overdue?.length ?? 0} urgency="overdue" />
        <StatCard label="Críticos (≤7 días)" value={summary.critical?.length ?? 0} urgency="critical" />
        <StatCard label="Próximos (≤30 días)" value={summary.warning?.length ?? 0} urgency="warning" />
        <StatCard label="En el radar" value={summary.upcoming?.length ?? 0} urgency="normal" />
      </div>

      {/* Critical section */}
      {criticalItems.length > 0 && (
        <section style={{ marginBottom: '3rem' }}>
          <div className="eyebrow" style={{ color: 'var(--danger-bright)', marginBottom: '1rem' }}>
            Atención inmediata
          </div>
          <h2 style={{ marginBottom: '1.5rem', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>
            Esto no puede esperar
          </h2>
          <div className="stack-sm">
            {criticalItems.map((ob: any) => (
              <ObligationRow key={ob.id} obligation={ob} />
            ))}
          </div>
        </section>
      )}

      {/* Warning section */}
      {warningItems.length > 0 && (
        <section style={{ marginBottom: '3rem' }}>
          <div className="eyebrow" style={{ marginBottom: '1rem' }}>Próximos 30 días</div>
          <div className="stack-sm">
            {warningItems.map((ob: any) => (
              <ObligationRow key={ob.id} obligation={ob} />
            ))}
          </div>
        </section>
      )}

      {criticalItems.length === 0 && warningItems.length === 0 && (
        <Empty
          title="Nada urgente esta semana"
          description="Todas tus obligaciones están al día o lejos de su vencimiento. Buen trabajo."
          action={
            <Link to="/timeline" className="btn btn-primary">
              Ver timeline completo
            </Link>
          }
        />
      )}
    </div>
  );
}

function StatCard({ label, value, urgency }: { label: string; value: number; urgency: string }) {
  return (
    <div className="card-raised">
      <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>{label}</div>
      <div
        className="numeric"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2.5rem',
          fontWeight: 400,
          fontVariationSettings: "'opsz' 72",
          color:
            urgency === 'overdue' || urgency === 'critical'
              ? 'var(--danger-bright)'
              : urgency === 'warning'
              ? 'var(--accent)'
              : 'var(--text)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ObligationRow({ obligation }: { obligation: any }) {
  return (
    <Link
      to={`/compliance/${obligation.jurisdiction}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: '1.5rem',
        alignItems: 'center',
        padding: '1.25rem 1.5rem',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${
          obligation.urgency === 'overdue'
            ? 'var(--danger-bright)'
            : obligation.urgency === 'critical'
            ? 'var(--danger)'
            : obligation.urgency === 'warning'
            ? 'var(--accent)'
            : 'var(--border-strong)'
        }`,
        borderRadius: 'var(--r-sm)',
        color: 'var(--text)',
        textDecoration: 'none',
        transition: 'background 0.15s',
      }}
    >
      <div>
        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{obligation.title}</div>
        <div className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {jurisdictionLabel(obligation.jurisdiction)} · {obligation.due_date}
        </div>
      </div>
      <div className="numeric" style={{ fontSize: '0.85rem', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
        {formatDaysUntil(obligation.days_until_due)}
      </div>
      <StatusPill urgency={obligation.urgency} label={urgencyLabel(obligation.urgency)} />
    </Link>
  );
}
